import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import toast, { Toaster } from "react-hot-toast";

// Components
import { SignupLeftView } from "./loginLeftView";
import Footer from "../dashboard/ProfileFooter";
import { Step1EmailVerification } from "./steps/Step1EmailVerification";
import { Step2BVNVerification } from "./steps/Step2BVNVerification";
import { Step4PasswordReferral } from "./steps/Step4PasswordReferral";

// Hooks
import { useMultiStepForm } from "../../hooks/useMultiStepForm";
import { getAllSources } from "../../store/actions";

// Styles
import { Wrapper, RightWrapper } from "./styled";
import { initRegistrationService, registerV2, verifyBVNService, verifyEmailService } from "../../services/authServices";

function MultiStepUserSignup() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const auth = useSelector((state) => state.auth);
  const { isLoading } = auth;
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { sources } = useSelector((state) => state.user_profile);

  const location = useLocation();
  const query = new URLSearchParams(location?.search);
  const referralCode = query.get("referralCode");
  const sourcer = query.get("source");

  // Use custom hook for form management
  const form = useMultiStepForm();
  const { resetForm, setUserType } = form;

  // ============ HANDLERS FOR EACH STEP ============



  const handleVerifyEmail = async () => {
    const errors = {};

    if (!form.otp) {
      errors.otp = "OTP is required";
    } else if (!/^\d{6}$/.test(form.otp)) {
      errors.otp = "OTP must be 6 digits";
    }

    if (Object.keys(errors).length > 0) {
      form.setStepErrors(errors);
      return;
    }

    try {
      const response = await verifyEmailService({
        email: form.email,
        code: form.otp,
      });

      if (response.status !== 200) {
        const errorData = await response.data;
        throw new Error(errorData.message || "OTP verification failed");
      }
      toast.success(`Email verified. Account secured for ${form.email}`, {
        duration: 5000,
      });
      JSON.stringify(localStorage.setItem("token", response?.data?.data));
      form.setEmailVerified(true);
      form.setEmailVerifiedAtStep(true); // Lock Step 1 after verification
      form.setStepErrors({});
      form.goToNextStep();
    } catch (error) {
      form.setStepErrors({ otp: error.message || "Failed to verify OTP" });
    }
  };

  const handleResendOtp = async () => {
    try {
      const payload = {
        email: form.email,
        userType: form.userType || "INDIVIDUAL_USER",
        ...(form.userType === "COMPANY" && form.companyName
          ? { companyName: form.companyName }
          : {}),
      };

      const response = await initRegistrationService(payload);

      // await fetch("/v2/auth/register/init", {
      //   method: "POST",
      //   headers: {
      //     "Content-Type": "application/json",
      //   },
      //   body: JSON.stringify(payload),
      // });

      if (response.status !== 200) {
        const errorData = await response.data;
        throw new Error(
          errorData.message || "Failed to initialize email registration"
        );
      }
      toast.success(response?.data?.message || "OTP sent to your email", {
        duration: 5000,

      });
      console.log("OTP resent to", form.email);
    } catch (error) {
      console.error("Resend OTP error:", error);
      form.setStepErrors({ otp: "Failed to resend OTP" });
    }
  };

  const handleVerifyBvn = async () => {
    const errors = form.validateBVN();

    if (Object.keys(errors).length > 0) {
      form.setStepErrors(errors);
      return;
    }

    form.setBvnVerifying(true);
    form.setBvnError("");
    form.setStepErrors({});

    try {
      // TODO: Replace with actual BVN verification endpoint
      const response = await verifyBVNService({ bvn: form.bvn, dateOfBirth: form.dob });
      if (response.status !== 200) {
        const errorData = await response.data;
        throw new Error(errorData.message || "BVN verification failed");
      }
      console.log("BVN verification response:", response);
      form.setBvnUserDetails(response?.data?.data);
      toast.success(`BVN verified for ${response?.data?.data?.firstName} ${response?.data?.data?.lastName}`, {
        duration: 5000,
      });
      form.setBvnVerifying(false);
      form.setBvnVerifiedAtStep(true); // Lock Step 2 after verification
      form.goToNextStep();
    } catch (error) {
      form.setBvnError(error.message || "Failed to verify BVN");
      form.setBvnVerifying(false);
    }
  };

  const handleFinalSubmit = async () => {
    const errors = form.validatePassword();

    // // Additional check: passwords must match
    if (form.password !== form.confirmPassword) {
      form.setStepErrors({
        ...errors,
        confirmPassword: "Passwords do not match"
      });
      return;
    }

    if (Object.keys(errors).length > 0) {
      form.setStepErrors(errors);
      return;
    }

    const completeFormData = form.getCompleteFormData();
    completeFormData.referralCode = referralCode;
    completeFormData.sourcer = sourcer;

    const payload = {
      bvn: form.bvn,
      dateOfBirth: form.bvnUserDetails?.dateOfBirth || form.dob,
      last4PhoneDigits: form.bvnUserDetails?.mobile?.slice(-4) || form.bvnLast4,
      email: form.email,
      usage: 'TREASURY',
      role: form.userType,
      password: form.password,
      source: form.source || 1,
      registrationChannel: 'WEB',
      referredBy: referralCode || form.referralCode || '',
    }
    setIsSubmitting(true);
    try {
      const response = await registerV2(payload);
      if (response.status !== 200) {
        const errorData = await response.data;
        toast.error(errorData.message || "Signup failed", {
          duration: 5000,
        });
        form.setStepErrors({ submit: errorData.message || "Signup failed" });
        return;
      }
      toast.success(response?.data?.message || "Account created successfully", {
        duration: 5000,
      });
      form.setStepErrors({});
      navigate("/congrates", { state: "success_signup" });
      // TODO: Call the actual signup endpoint with completeFormData
      // dispatch(signupUser(completeFormData));

      form.setStepErrors({});
    } catch (error) {
      form.setStepErrors({
        submit: error?.response?.data?.message || error?.message || "Signup failed",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced back navigation with validation to prevent editing verified steps
  const handleGoBack = () => {
    const result = form.goToPreviousStep();
    if (result?.blocked) {
      toast.error(result.reason, { duration: 4000 });
      return;
    }
  };

  useEffect(() => {
    dispatch(getAllSources());
  }, [dispatch]);

  // Detect route and set userType accordingly, reset form on route change
  useEffect(() => {
    resetForm();
    const pathname = location.pathname;
    if (pathname.includes("register-company")) {
      setUserType("COMPANY");
    } else if (pathname.includes("register-user")) {
      setUserType("INDIVIDUAL_USER");
    } else {
      setUserType("INDIVIDUAL_USER"); // Default
    }
  }, [location.pathname, resetForm, setUserType]);

  // Auto-set source from URL parameter if it matches an available source
  // useEffect(() => {
  //   if (sourcer && sources && sources.length > 0) {
  //     const matchedSource = sources.find(
  //       (source) => source.name.toLowerCase() === sourcer.toLowerCase() || source.id === sourcer
  //     );
  //     if (matchedSource) {
  //       form.setReferralOption(matchedSource.id);
  //     } else {
  //       // If source doesn't match any available option, set it as "other"
  //       form.setSourceNotInList(sourcer);
  //     }
  //   }
  // }, [sourcer, sources, form]);

  return (
    <div>
      <div>
        <Toaster
        // toastOptions={{
        //   className: "bg-danger text-white",
        // }}
        />
      </div>
      <Footer source={sourcer} referralCode={referralCode} />
      <Wrapper>
        <div className="content">
          <div className="login-left-view">
            <SignupLeftView source={sourcer} referralCode={referralCode} />
          </div>

          <div className="login-right-view">
            <div className="login-right-inner">
              <RightWrapper>
                <h4 className="container">Sign up</h4>
                <div className="container">
                  {/* <StepProgressBar currentStep={form.currentStep} /> */}

                  <form
                    autoComplete="off"
                    onSubmit={(e) => {
                      e.preventDefault();
                      handleFinalSubmit();
                    }}
                  >
                    {form.currentStep === 1 && (
                      <Step1EmailVerification
                        email={form.email}
                        setEmail={form.setEmail}
                        otp={form.otp}
                        setOtp={form.setOtp}
                        emailVerified={form.emailVerified}
                        stepErrors={form.stepErrors}
                        setStepErrors={form.setStepErrors}
                        onVerify={handleVerifyEmail}
                        onResendOtp={handleResendOtp}
                        isLoading={isLoading}
                        // userStore={userStore}
                        userType={form.userType}
                        form={form}
                      />
                    )}
                    {form.currentStep === 2 && (
                      <Step2BVNVerification
                        bvn={form.bvn}
                        setBvn={form.setBvn}
                        dob={form.dob}
                        setDob={form.setDob}
                        bvnUserDetails={form.bvnUserDetails}
                        bvnError={form.bvnError}
                        stepErrors={form.stepErrors}
                        setStepErrors={form.setStepErrors}
                        setBvnError={form.setBvnError}
                        onVerify={handleVerifyBvn}
                        onPrevious={handleGoBack}
                        isVerifying={form.bvnVerifying}
                        userType={form.userType}
                      />
                    )}
                    {/* 
                    {form.currentStep === 3 && (
                      <Step3BVNConfirmation
                        bvnLast4={form.bvnLast4}
                        setBvnLast4={form.setBvnLast4}
                        bvnUserDetails={form.bvnUserDetails}
                        stepErrors={form.stepErrors}
                        setStepErrors={form.setStepErrors}
                        onVerify={handleVerifyBvnLast4}
                        onPrevious={form.goToPreviousStep}
                        isVerified={form.bvnVerified}
                      />
                    )} */}

                    {form.currentStep === 3 && (
                    <Step4PasswordReferral
                      password={form.password}
                      setPassword={form.setPassword}
                      confirmPassword={form.confirmPassword}
                      setConfirmPassword={form.setConfirmPassword}
                      passwordShown1={form.passwordShown1}
                      setPasswordShown1={form.setPasswordShown1}
                      passwordShown2={form.passwordShown2}
                      setPasswordShown2={form.setPasswordShown2}
                      referralOption={form.referralOption}
                      setReferralOption={form.setReferralOption}
                      isUserNewsLetters={form.isUserNewsLetters}
                      setIsUserNewsLetters={form.setIsUserNewsLetters}
                      isUserTerms={form.isUserTerms}
                      setIsUserTerms={form.setIsUserTerms}
                      sources={sources}
                      stepErrors={form.stepErrors}
                      setStepErrors={form.setStepErrors}
                      onPrevious={handleGoBack}
                      onSubmit={handleFinalSubmit}
                      isLoading={isLoading || isSubmitting}
                    />
                    )}
                  </form>
                </div>
              </RightWrapper>
            </div>
          </div>
        </div>
      </Wrapper>
    </div>
  );
}

export default MultiStepUserSignup;
